import React, { useState, useEffect } from 'react';
import PageLayout from '../../../../components/PageLayout';
import AssignStudentsToAid from './AssignStudentsToAid';
import CreateProgramForm from './CreateProgramForm';
import LinkBillItems from './LinkBillItems';
import { useHistory, useLocation } from 'react-router-dom';
import {
  createAidProgram,
  updateAidProgram,
  updateBillItems,
  assignStudents,
  getAidProgram,
  getProgramBillItems,
  getProgramStudents,
} from '../financial-aid.service';

export default function CreateFinancialAidPage() {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [currentStep, setCurrentStep] = useState(() => {
    const stepParam = searchParams.get('step');
    const editId = searchParams.get('id');
    return editId && stepParam ? parseInt(stepParam, 10) : 1;
  });
  const [programData, setProgramData] = useState(null);
  const [linkedData, setLinkedData] = useState(null);
  const [assignedStudentsData, setAssignedStudentsData] = useState(null);

  // API state management
  const [programId, setProgramId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorStep, setErrorStep] = useState(null); // Which step failed: 1, 2, or 3

  // Get program ID from query string for edit mode
  const editProgramId = searchParams.get('id');

  // Load existing program data if in edit mode
  useEffect(() => {
    if (editProgramId) {
      setProgramId(editProgramId);
      loadExistingProgram(editProgramId);
    }
  }, [editProgramId]);

  const loadExistingProgram = async (id) => {
    setIsLoading(true);
    try {
      // Fetch program details
      const programResult = await getAidProgram(id);
      if (programResult.success && programResult.program) {
        const p = programResult.program;
        // Transform API response to form data format
        setProgramData({
          programName: p.name,
          description: p.description,
          aidType: p.type,
          amountType: p.amountType,
          amount: p.amount.toString(),
          academicYearId: p.academicYearId.toString(),
          termId: p.termId ? p.termId.toString() : '',
        });

        // Fetch linked bill items
        const billItemsResult = await getProgramBillItems(id);
        if (billItemsResult.success && billItemsResult.billItems) {
          const selectedBillItems = billItemsResult.billItems.map(
            (item) => item.billingFeeId,
          );
          const overrideAmounts = {};
          billItemsResult.billItems.forEach((item) => {
            if (item.overrideAmount !== null) {
              overrideAmounts[item.billingFeeId] =
                item.overrideAmount.toString();
            }
          });
          setLinkedData({ selectedBillItems, overrideAmounts });
        }

        // Fetch assigned students
        const studentsResult = await getProgramStudents(id);
        if (studentsResult.success && studentsResult.students) {
          const assignedStudents = studentsResult.students.map((s) => ({
            // Use studentId as both id and studentId (numeric ID from API)
            id: s.studentId?.toString(),
            studentId: s.studentId?.toString(),
            fullName: s.studentName || s.name,
            class: s.class,
            studentUniqueId: s.studentUniqueId,
          }));

          const overrideAmounts = {};
          studentsResult.students.forEach((s) => {
            if (s.overrideAmount !== null) {
              // Use studentId (numeric) as key
              const key = s.studentId?.toString();
              overrideAmounts[key] = s.overrideAmount.toString();
            }
          });

          setAssignedStudentsData({ assignedStudents, overrideAmounts });
        }
      }
    } catch (err) {
      setError('Failed to load program data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async (data) => {
    // Clear any previous errors when moving to next step
    setError(null);
    setErrorStep(null);

    if (currentStep === 1) {
      setProgramData(data);
      // When moving from step 1 to step 2, we need to create or update the program
      setIsLoading(true);
      try {
        let result;
        if (programId) {
          // Update existing program
          result = await updateAidProgram(programId, data);
        } else {
          // Create new program
          result = await createAidProgram(data);
        }

        if (!result.success) {
          setError(result.message || 'Failed to save program');
          setErrorStep(1);
          setIsLoading(false);
          return;
        }

        // Get the program ID from the response (for new programs)
        if (!programId && result.programId) {
          setProgramId(result.programId);
        }
      } catch (err) {
        setError(err.message || 'An unexpected error occurred');
        setErrorStep(1);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    } else if (currentStep === 2) {
      setLinkedData(data);
      // Persist step 2 data to API immediately
      setIsLoading(true);
      try {
        const result = await updateBillItems(programId, data);

        if (!result.success) {
          setError(result.message || 'Failed to link bill items');
          setErrorStep(2);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        setError(err.message || 'An unexpected error occurred');
        setErrorStep(2);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setErrorStep(null);
    setCurrentStep((prev) => prev - 1);
  };

  const handleBackFromStep3 = (data) => {
    setAssignedStudentsData(data);
    setError(null);
    setErrorStep(null);
    setCurrentStep((prev) => prev - 1);
  };

  const handleFinish = async (data) => {
    setIsLoading(true);
    setError(null);
    setErrorStep(null);
    setAssignedStudentsData(data);

    try {
      // Steps 1 and 2 are already persisted in handleNext
      // Just need to persist step 3 (students)
      // Step 3: Assign students
      const step3Result = await assignStudents(programId, data);

      if (!step3Result.success) {
        setError(step3Result.message || 'Failed to assign students');
        setErrorStep(3);
        setCurrentStep(3); // Stay on step 3 for retry
        setIsLoading(false);
        return;
      }

      // All steps completed successfully
      console.log('Financial Aid Program Created Successfully:', {
        programId: programId,
        program: programData,
        linkedItems: linkedData,
        assignedStudents: data,
      });

      history.push('/financial-aid');
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      setErrorStep(1); // Default to step 1 on unexpected errors
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async () => {
    if (errorStep === 1) {
      // Retry step 1 only
      setIsLoading(true);
      setError(null);

      try {
        let step1Result;
        if (programId) {
          // Update existing program
          step1Result = await updateAidProgram(programId, programData);
        } else {
          // Create new program
          step1Result = await createAidProgram(programData);
        }

        if (!step1Result.success) {
          setError(step1Result.message || 'Failed to save program');
          setErrorStep(1);
          setIsLoading(false);
          return;
        }

        // Get the program ID from the response (for new programs)
        if (!programId && step1Result.programId) {
          setProgramId(step1Result.programId);
        }

        // After step 1 succeeds, retry step 2
        const step2Result = await updateBillItems(programId, linkedData);

        if (!step2Result.success) {
          setError(step2Result.message || 'Failed to link bill items');
          setErrorStep(2);
          setCurrentStep(2);
          setIsLoading(false);
          return;
        }

        // After step 2 succeeds, retry step 3
        const step3Result = await assignStudents(
          programId,
          assignedStudentsData,
        );

        if (!step3Result.success) {
          setError(step3Result.message || 'Failed to assign students');
          setErrorStep(3);
          setCurrentStep(3);
          setIsLoading(false);
          return;
        }

        // All steps completed successfully
        history.push('/financial-aid');
      } catch (err) {
        setError(err.message || 'An unexpected error occurred');
        setErrorStep(1);
      } finally {
        setIsLoading(false);
      }
    } else if (errorStep === 2) {
      // Retry step 2 only
      setIsLoading(true);
      setError(null);

      try {
        const step2Result = await updateBillItems(programId, linkedData);

        if (!step2Result.success) {
          setError(step2Result.message || 'Failed to link bill items');
          setErrorStep(2);
          setIsLoading(false);
          return;
        }

        // After step 2 succeeds, retry step 3
        const step3Result = await assignStudents(
          programId,
          assignedStudentsData,
        );

        if (!step3Result.success) {
          setError(step3Result.message || 'Failed to assign students');
          setErrorStep(3);
          setCurrentStep(3);
          setIsLoading(false);
          return;
        }

        // All steps completed successfully
        history.push('/financial-aid');
      } catch (err) {
        setError(err.message || 'An unexpected error occurred');
        setErrorStep(2);
      } finally {
        setIsLoading(false);
      }
    } else if (errorStep === 3) {
      // Retry step 3 only
      setIsLoading(true);
      setError(null);

      try {
        const step3Result = await assignStudents(
          programId,
          assignedStudentsData,
        );

        if (!step3Result.success) {
          setError(step3Result.message || 'Failed to assign students');
          setErrorStep(3);
          setIsLoading(false);
          return;
        }

        // Completed successfully
        history.push('/financial-aid');
      } catch (err) {
        setError(err.message || 'An unexpected error occurred');
        setErrorStep(3);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    history.push('/financial-aid');
  };

  const isEditMode = !!editProgramId;

  return (
    <PageLayout
      title={isEditMode ? 'Edit Financial Aid' : 'Create Financial Aid'}
      breadcrumbs={[
        { label: 'Billing', to: '/billing' },
        { label: `Financial Aid Programs`, to: '/financial-aid' },
        ...(isEditMode
          ? [
              {
                label: programData?.programName,
                to: `/financial-aid/single/${programId}`,
              },
            ]
          : []),
        {
          label: isEditMode ? 'Edit Financial Aid' : 'Create Financial Aid',
          active: true,
        },
      ]}
      isLoading={isLoading}
    >
      {/* Error Alert */}
      {error && (
        <div
          style={{
            padding: '16px',
            marginBottom: '20px',
            maxWidth: '800px',
            margin: '0 auto 20px auto',
          }}
        >
          <p className="text-14  text-danger font-bold">{error}</p>
        </div>
      )}

      {(!editProgramId || programData) && currentStep === 1 && (
        <CreateProgramForm
          onSave={handleNext}
          onCancel={handleCancel}
          initialData={programData}
          isLoading={isLoading}
          hasError={errorStep === 1}
        />
      )}
      {(!editProgramId || programData) && currentStep === 2 && (
        <LinkBillItems
          program={programData}
          onSave={handleNext}
          onBack={handleBack}
          initialData={linkedData}
          isLoading={isLoading}
          hasError={errorStep === 2}
          isEditMode={isEditMode}
          programId={programId}
        />
      )}
      {(!editProgramId || programData) && currentStep === 3 && (
        <AssignStudentsToAid
          program={programData}
          linkedData={linkedData}
          onFinish={handleFinish}
          onBack={handleBackFromStep3}
          initialData={assignedStudentsData}
          isLoading={isLoading}
          hasError={errorStep === 3}
          isEditMode={isEditMode}
          programId={programId}
          classFilterFromStep2={linkedData?.selectedClassId}
        />
      )}
    </PageLayout>
  );
}
